import { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../../config/api';

// Cache for storing fetched recent jobs data
let recentJobsCache = null;
let lastRecentFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** Called by overviewPrefetch to warm cache so Overview loads instantly. */
export function setRecentJobsCacheFromPrefetch(mappedJobs, limit = 10) {
  if (!Array.isArray(mappedJobs)) return;
  recentJobsCache = mappedJobs.slice(0, limit);
  lastRecentFetchTime = Date.now();
}

const useRecentJobs = (limit = 10) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const fetchRecentJobs = async () => {
      // Check if we have valid cached data
      const now = Date.now();
      if (recentJobsCache && (now - lastRecentFetchTime) < CACHE_DURATION) {
        setJobs(recentJobsCache);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Get the employer email from localStorage (set during login)
        const employerEmail = localStorage.getItem('employer_email');
        
        if (!employerEmail) {
          setLoading(false);
          return;
        }

        // Use the same endpoint as jobHook but limit the results
        const response = await fetch(getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(employerEmail)}`), {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch recent jobs");
        }

        const data = await response.json();
        
        // Limit the results to the specified limit (jobs are already ordered by created_at DESC)
        const limitedJobs = data ? data.slice(0, limit) : [];
        
        // Map backend properties to frontend expected names (same as jobHook.js)
        const mappedJobs = (limitedJobs || []).map(job => ({
          id: job.job_id,
          jobTitle: job.job_title,
          jobType: job.job_type,
          experienceRange: job.experience_range,
          location: job.location,
          status: job.status,
          expirationDate: job.expirationDate || job.expiration_date,
          applications: job.applicants,
          ...job // keep all original properties too
        }));
        
        // Cache the fetched data
        recentJobsCache = mappedJobs;
        lastRecentFetchTime = now;
        
        setJobs(mappedJobs);
      } catch (err) {
        console.error("Error fetching recent jobs:", err.message);
        setError(err.message || 'Failed to fetch recent jobs.');
        // If there's an error but we have cached data, use that instead
        if (recentJobsCache) {
          setJobs(recentJobsCache);
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch on initial mount or if cache is expired
    if (isInitialMount.current || !recentJobsCache || (Date.now() - lastRecentFetchTime) >= CACHE_DURATION) {
      fetchRecentJobs();
      isInitialMount.current = false;
    } else {
      // Use cached data immediately
      setJobs(recentJobsCache);
      setLoading(false);
      setError(null);
    }
  }, [limit]);

  // Function to refresh recent jobs
  const refreshRecentJobs = async () => {
    // Clear cache to force fresh fetch
    recentJobsCache = null;
    lastRecentFetchTime = 0;
    setLoading(true);
    setError(null);
    
    const employerEmail = localStorage.getItem('employer_email');
    if (!employerEmail) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(employerEmail)}`), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch recent jobs");
      }

      const data = await response.json();
      
      const limitedJobs = data ? data.slice(0, limit) : [];
      const mappedJobs = (limitedJobs || []).map(job => ({
        id: job.job_id,
        jobTitle: job.job_title,
        jobType: job.job_type,
        experienceRange: job.experience_range,
        location: job.location,
        status: job.status,
        expirationDate: job.expirationDate || job.expiration_date,
        applications: job.applicants,
        ...job
      }));
      
      recentJobsCache = mappedJobs;
      lastRecentFetchTime = Date.now();
      setJobs(mappedJobs);
    } catch (err) {
      console.error("Error refreshing recent jobs:", err.message);
      setError(err.message || 'Failed to fetch recent jobs.');
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    recentJobsCache = null;
    lastRecentFetchTime = 0;
  };

  return { jobs, loading, error, refreshRecentJobs, clearCache };
};

/** Clear cache on logout so next user doesn't see stale data. */
export function clearRecentJobsCacheOnLogout() {
  recentJobsCache = null;
  lastRecentFetchTime = 0;
}

export default useRecentJobs;
